<template>
  <div :id="mapId" class="point-cloud-container">
    <div v-if="loading" class="pc-status">
      <b-spinner small class="mr-2"></b-spinner>{{ loadingLabel }}
    </div>
    <div v-if="error" class="pc-status pc-status-error">{{ error }}</div>
  </div>
</template>

<script>
// Demo-only 3D viewer: renders a colored point cloud of the forest with
// Three.js. Hardcoded to a single demo file for now (not per-area data) —
// mounted lazily (v-if from the parent) so the Three.js bundle and the
// download only happen once the "3D" tab is actually clicked.
//
// The source file is a 3D Gaussian Splatting .ply (479MB — position,
// normals, spherical-harmonics color, opacity, scale, rotation, 19 float
// properties/vertex) but this viewer only ever needs position + color, so
// scripts/compact-point-cloud.js (new-server) pre-converts it once into a
// small custom binary (.zcpc, ~90MB — see that script for the exact byte
// layout) that's trivial to parse here: no PLY header, no per-vertex SH
// math, just position floats + already-baked RGB bytes.
//
// Served from our own Strapi (uploads/point-cloud-demo/ of the media bucket) rather
// than fetched straight from Dropbox/GCS: Dropbox's folder-share links
// don't send CORS headers on their first redirect hop, and the GCS bucket
// wasn't public, so a browser fetch() failed against both. Serving it
// same-infra as everything else sidesteps that entirely, and lets a long
// Cache-Control apply so repeat visits don't re-download it at all.
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

const DEMO_PLY_URL = process.env.VUE_APP_IMAGE_URL + "/uploads/point-cloud-demo/point_cloud.zcpc";

async function fetchWithProgress(url, onProgress) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const total = parseInt(response.headers.get("content-length") || "0", 10);
  if (!response.body || !total) return response.arrayBuffer();

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress(received, total);
  }
  const buffer = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    buffer.set(chunk, offset);
    offset += chunk.length;
  }
  return buffer.buffer;
}

// Mirrors scripts/compact-point-cloud.js's output format exactly:
// magic "ZCPC" (4 bytes) + version (1 byte) + vertex count uint32 LE (4
// bytes), then per vertex: float32 x, float32 y, float32 z, uint8 r,g,b.
function parseCompactPointCloud(buffer) {
  const view = new DataView(buffer);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== "ZCPC") throw new Error("Not a valid point-cloud file (bad magic)");

  const vertexCount = view.getUint32(5, true);
  const stride = 15;
  const dataStart = 9;

  const positions = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 3);

  for (let i = 0; i < vertexCount; i++) {
    const base = dataStart + i * stride;
    positions[i * 3] = view.getFloat32(base, true);
    positions[i * 3 + 1] = view.getFloat32(base + 4, true);
    positions[i * 3 + 2] = view.getFloat32(base + 8, true);

    colors[i * 3] = view.getUint8(base + 12) / 255;
    colors[i * 3 + 1] = view.getUint8(base + 13) / 255;
    colors[i * 3 + 2] = view.getUint8(base + 14) / 255;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geometry;
}

export default {
  name: "PointCloud3DComponent",

  props: {
    mapId: {
      required: false,
      type: String,
      default: "point-cloud-3d"
    }
  },

  data() {
    return {
      loading: true,
      loadingLabel: "Downloading 3D point cloud…",
      error: null,
      renderer: null,
      scene: null,
      camera: null,
      controls: null,
      animationFrame: null,
      resizeObserver: null
    };
  },

  mounted() {
    this.init();
  },

  beforeDestroy() {
    this.teardown();
  },

  methods: {
    async init() {
      const container = document.getElementById(this.mapId);
      if (!container) return;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x1a2a1d);

      this.camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.01, 5000);
      this.camera.position.set(0, 0, 5);

      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setPixelRatio(window.devicePixelRatio || 1);
      this.renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(this.renderer.domElement);

      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;

      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(container);

      this.animate();

      try {
        const buffer = await fetchWithProgress(DEMO_PLY_URL, (received, total) => {
          const pct = Math.round((received / total) * 100);
          this.loadingLabel = `Downloading 3D point cloud… ${pct}% (${(total / 1024 / 1024).toFixed(0)} MB)`;
        });
        this.loadingLabel = "Parsing point cloud…";
        // Let the "Parsing…" label paint before the synchronous parse blocks the thread.
        await new Promise((resolve) => setTimeout(resolve, 30));
        const geometry = parseCompactPointCloud(buffer);
        this.onLoaded(geometry);
      } catch (err) {
        console.error("Failed to load point cloud:", err);
        this.loading = false;
        this.error = "Could not load the 3D point cloud: " + (err.message || err);
      }
    },

    onLoaded(geometry) {
      geometry.computeBoundingBox();

      const material = new THREE.PointsMaterial({ size: 0.015, vertexColors: true });
      const points = new THREE.Points(geometry, material);

      // Center the cloud at the origin and frame the camera to fit it,
      // since we don't know this file's scale/units ahead of time.
      const box = geometry.boundingBox;
      const center = new THREE.Vector3();
      box.getCenter(center);
      points.position.sub(center);

      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      this.camera.position.set(0, maxDim * 0.3, maxDim * 1.5);
      this.camera.far = maxDim * 10;
      this.camera.updateProjectionMatrix();
      this.controls.target.set(0, 0, 0);
      this.controls.update();

      this.scene.add(points);
      this.loading = false;
    },

    animate() {
      this.animationFrame = requestAnimationFrame(this.animate);
      if (this.controls) this.controls.update();
      if (this.renderer && this.scene && this.camera) this.renderer.render(this.scene, this.camera);
    },

    onResize() {
      const container = document.getElementById(this.mapId);
      if (!container || !this.renderer || !this.camera) return;
      this.camera.aspect = container.clientWidth / container.clientHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(container.clientWidth, container.clientHeight);
    },

    teardown() {
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.controls) this.controls.dispose();
      if (this.renderer) {
        this.renderer.dispose();
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
      }
      if (this.scene) {
        this.scene.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) obj.material.dispose();
        });
      }
    }
  }
};
</script>

<style scoped>
.point-cloud-container {
  position: relative;
  height: 100%;
  width: 100%;
}

.pc-status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.pc-status-error {
  max-width: 80%;
  white-space: normal;
  text-align: center;
  color: #e88;
}
</style>
